import type { DataProvider, OHLCVRequest } from './types';
import { ProviderError } from './types';
import type { Exchange } from '../types/exchange';
import type { TradingPair } from '../types/pair';
import type { OHLCV, Timeframe } from '../types/ohlcv';
import { aggregateCandles, aggregateToWeekly } from '../utils/aggregate';
import { timeframeToMs } from '../utils/timeframe';

/**
 * Coinbase Exchange public market data (no API key, CORS enabled — verified
 * live 2026-10-01: `access-control-allow-origin: *` on /products/{id}/candles).
 *
 * Endpoint facts this provider relies on:
 *   - GET /products/{BASE-QUOTE}/candles?granularity=S&start=ISO&end=ISO
 *     returns `[time (unix s), low, high, open, close, volume]` rows, NEWEST
 *     first, at most 300 rows per call; granularity must be one of
 *     60, 300, 900, 3600, 21600, 86400 seconds.
 *   - 4h and 1W candles do not exist upstream; they are aggregated here from
 *     1h and 1D respectively (UTC-aligned buckets).
 *   - GET /products lists markets with base_currency / quote_currency /
 *     quote_increment / status.
 *   - GET /products/{id}/ticker gives the last trade price.
 */
export interface CoinbaseProviderOptions {
  /** Defaults to https://api.exchange.coinbase.com */
  baseUrl?: string;
  /** Custom fetch (tests, server-side use, proxies). */
  fetch?: typeof fetch;
  /** Only list products with this quote currency in `getPairs` (e.g. ['USD']). */
  quoteFilter?: string[];
}

const DEFAULT_BASE_URL = 'https://api.exchange.coinbase.com';
const MAX_ROWS = 300;

const NATIVE_GRANULARITY: Partial<Record<Timeframe, number>> = {
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '1h': 3600,
  '1D': 86400,
};

/** Timeframes Coinbase has no granularity for, and what to build them from. */
const DERIVED: Partial<Record<Timeframe, Timeframe>> = {
  '4h': '1h',
  '1W': '1D',
};

export function toCoinbaseProductId(pair: string): string {
  return pair.trim().toUpperCase().replace('/', '-');
}

type Row = [number, number, number, number, number, number];

export function mapCoinbaseCandles(rows: unknown): OHLCV[] {
  if (!Array.isArray(rows)) throw new ProviderError('Unrecognized Coinbase candle response');
  const out: OHLCV[] = [];
  for (const row of rows as Row[]) {
    if (!Array.isArray(row) || row.length < 6) continue;
    const [time, low, high, open, close, volume] = row;
    out.push({ timestamp: time * 1000, open, high, low, close, volume });
  }
  // Newest-first upstream; LWC and the rest of HopCharts want oldest-first.
  out.sort((a, b) => a.timestamp - b.timestamp);
  return out;
}

/**
 * Split `[from, to]` into windows of at most MAX_ROWS candles so every call
 * stays under Coinbase's per-request cap. Windows are returned oldest-first.
 */
export function chunkRange(from: number, to: number, stepMs: number): Array<{ start: number; end: number }> {
  const windows: Array<{ start: number; end: number }> = [];
  const span = MAX_ROWS * stepMs;
  let start = Math.max(0, from);
  while (start <= to) {
    const end = Math.min(to, start + span - stepMs);
    windows.push({ start, end });
    start = end + stepMs;
  }
  return windows;
}

export function coinbaseProvider(options: CoinbaseProviderOptions = {}): DataProvider {
  const baseUrl = (options.baseUrl ?? DEFAULT_BASE_URL).replace(/\/$/, '');
  const quoteFilter = options.quoteFilter?.map((q) => q.toUpperCase());

  async function getJson<T>(path: string): Promise<T> {
    const doFetch = options.fetch ?? globalThis.fetch;
    const response = await doFetch(`${baseUrl}${path}`, { headers: { Accept: 'application/json' } });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const message =
        body && typeof body === 'object' && typeof (body as { message?: unknown }).message === 'string'
          ? (body as { message: string }).message
          : `Coinbase request failed (${response.status})`;
      throw new ProviderError(message, response.status, body);
    }
    return (await response.json()) as T;
  }

  async function fetchNative(productId: string, granularity: number, from: number, to: number): Promise<OHLCV[]> {
    const stepMs = granularity * 1000;
    const windows = chunkRange(from, to, stepMs);
    const batches = await Promise.all(
      windows.map(({ start, end }) => {
        const query = new URLSearchParams({
          granularity: String(granularity),
          start: new Date(start).toISOString(),
          end: new Date(end).toISOString(),
        });
        return getJson<unknown>(`/products/${productId}/candles?${query}`).then(mapCoinbaseCandles);
      }),
    );
    const merged = new Map<number, OHLCV>();
    for (const batch of batches) for (const candle of batch) merged.set(candle.timestamp, candle);
    return [...merged.values()].sort((a, b) => a.timestamp - b.timestamp);
  }

  return {
    id: 'coinbase',

    async getOHLCV(request: OHLCVRequest): Promise<OHLCV[]> {
      const productId = toCoinbaseProductId(request.pair);
      const source = DERIVED[request.timeframe];
      if (source) {
        // Fetch the finer candles for the same window and roll them up.
        const bucketMs = timeframeToMs(request.timeframe);
        const alignedFrom = Math.floor(request.from / bucketMs) * bucketMs;
        const fine = await fetchNative(productId, NATIVE_GRANULARITY[source]!, alignedFrom, request.to);
        return request.timeframe === '1W' ? aggregateToWeekly(fine) : aggregateCandles(fine, bucketMs);
      }
      const granularity = NATIVE_GRANULARITY[request.timeframe];
      if (!granularity) throw new ProviderError(`Unsupported timeframe ${request.timeframe}`);
      return fetchNative(productId, granularity, request.from, request.to);
    },

    async getExchanges(): Promise<Exchange[]> {
      return [
        {
          id: 'coinbase',
          name: 'Coinbase',
          logoUrl: '',
          supportedFeatures: { websocket: false, ohlcv: true, orderbook: false },
        },
      ];
    },

    async getPairs(): Promise<TradingPair[]> {
      interface Product {
        id: string;
        base_currency: string;
        quote_currency: string;
        quote_increment: string;
        base_increment: string;
        status: string;
        trading_disabled?: boolean;
      }
      const products = await getJson<Product[]>('/products');
      return products
        .filter((p) => p.status === 'online' && !p.trading_disabled)
        .filter((p) => !quoteFilter || quoteFilter.includes(p.quote_currency.toUpperCase()))
        .map((p) => ({
          symbol: `${p.base_currency}/${p.quote_currency}`,
          base: p.base_currency,
          quote: p.quote_currency,
          exchangeId: 'coinbase',
          priceDecimals: decimalsOf(p.quote_increment),
          quantityDecimals: decimalsOf(p.base_increment),
        }))
        .sort((a, b) => a.symbol.localeCompare(b.symbol));
    },

    async getTicker(_exchangeId, pair) {
      const raw = await getJson<{ price?: string }>(`/products/${toCoinbaseProductId(pair)}/ticker`);
      return { last: Number.parseFloat(raw.price ?? '0') || 0 };
    },
  };
}

function decimalsOf(increment: string | undefined): number {
  if (!increment) return 8;
  const value = Number.parseFloat(increment);
  if (!Number.isFinite(value) || value <= 0) return 8;
  return Math.max(0, Math.round(-Math.log10(value)));
}
